import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AimOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useUserSettings } from '../../contexts/UserSettingsContext';
import { captureEvent } from '../../services/analytics';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { showErrorToastFullScreen } from '../../utils/toastUtils';
import { nextWiderStep, WidenableField, WidenLever } from '../../utils/searchPreferences';
import { UserSettings } from '../../types';
import css from './WidenSearchStrip.module.scss';

interface WidenSearchStripProps {
  // Non-null by contract — the host only renders this once settings have
  // loaded, so no control here can save a placeholder over a stored value.
  settings: UserSettings;
  // Opportunities this search has ever surfaced, not the pending-review count:
  // a user who worked through a healthy queue has cleared it to zero and their
  // net is fine.
  lifetimeYield: number;
  // Fired on a successful widen. A save removes the very room that made this
  // strip eligible, so without this the host's gate would unmount it mid-click
  // and the user would see their action vanish instead of land.
  onWidened: () => void;
  onDismiss: () => void;
}

/**
 * Point-of-need discovery for Search Preferences (design: settings discovery,
 * 2026-07-26). Both preferences are ordinal scales and the felt problem is
 * directional — the net is too tight — so this offers the single step out
 * rather than linking to Settings.
 *
 * A lever already at its widest renders as a muted, non-interactive line. That
 * keeps the offer honest (with the shipped defaults, match level is ALREADY
 * the loosest) while still teaching that the setting exists and where the user
 * stands on it.
 */
const WidenSearchStrip: React.FC<WidenSearchStripProps> = ({
  settings,
  lifetimeYield,
  onWidened,
  onDismiss,
}) => {
  const { updateSettings } = useUserSettings();
  const [savingField, setSavingField] = useState<string | null>(null);
  // Per-field, not one flag for the whole card: a user who tightened BOTH
  // preferences gets two actionable rows, and collapsing the card on the first
  // save would destroy the second before they could reach it.
  const [widenedTo, setWidenedTo] = useState<Partial<Record<WidenableField, string>>>({});

  const levers = nextWiderStep(settings);
  const anyApplied = Object.keys(widenedTo).length > 0;

  // One impression per mount. Keying this on lifetimeYield would re-fire every
  // time a notification refresh changed the count, inflating the shown → applied
  // funnel this card is measured by.
  const shownYield = useRef(lifetimeYield);
  useEffect(() => {
    captureEvent('widen_search_shown', { lifetime_yield: shownYield.current });
  }, []);

  const handleWiden = async (lever: WidenLever) => {
    if (savingField || !lever.next) return;
    setSavingField(lever.field);
    try {
      await updateSettings({ [lever.field]: lever.next } as Partial<UserSettings>);
      setWidenedTo((applied) => ({ ...applied, [lever.field]: lever.nextLabel as string }));
      onWidened();
      captureEvent('widen_search_applied', {
        field: lever.field,
        from: lever.currentLabel,
        to: lever.nextLabel,
      });
    } catch (error) {
      // No optimistic write to roll back — the row simply stays where it was.
      showErrorToastFullScreen(getApiErrorMessage(error));
    } finally {
      setSavingField(null);
    }
  };

  const handleDismiss = () => {
    captureEvent('widen_search_dismissed', { lifetime_yield: lifetimeYield });
    onDismiss();
  };

  return (
    <div className={css.strip} role="status">
      <div className={css.head}>
        <AimOutlined className={css.icon} aria-hidden="true" />
        <p className={css.diagnosis}>
          {lifetimeYield === 0
            ? 'Nothing cleared your filters yet.'
            : `Only ${lifetimeYield} ${lifetimeYield === 1 ? 'match' : 'matches'} cleared your filters.`}
        </p>
        <button
          type="button"
          className={css.dismiss}
          onClick={handleDismiss}
          aria-label="Dismiss widen-search suggestion"
        >
          <CloseOutlined />
        </button>
      </div>

      <ul className={css.levers}>
        {levers.map((lever) => (
          <li key={lever.field} className={css.lever}>
            <span className={css.legend}>{lever.legend}</span>

            {widenedTo[lever.field] ? (
              // Confirmed in place, so the OTHER row stays offered. Says what
              // the user did rather than restating the new value, which
              // lever.currentLabel would already show.
              <span className={css.applied}>
                <CheckOutlined className={css.appliedTick} aria-hidden="true" />
                Widened to {widenedTo[lever.field]}
              </span>
            ) : lever.next ? (
              <>
                <span className={css.track}>
                  <span className={css.from}>{lever.currentLabel}</span>
                  <span className={css.rail} aria-hidden="true" />
                  <span className={css.to}>{lever.nextLabel}</span>
                </span>
                <button
                  type="button"
                  className={css.widen}
                  onClick={() => handleWiden(lever)}
                  disabled={savingField !== null}
                >
                  {savingField === lever.field ? 'Widening…' : 'Widen'}
                </button>
              </>
            ) : (
              <span className={css.maxed}>{lever.currentLabel} — already widest</span>
            )}
          </li>
        ))}
      </ul>

      {anyApplied && (
        // Not "on the next refresh": the user would watch a recrawl finish and
        // see nothing, which is the exact dead-end this line exists to prevent.
        <p className={css.refreshNote}>
          You'll see the difference in tomorrow's matches.
        </p>
      )}

      {/* These are real, permanent settings — say where they live, so widening
          here doesn't feel like a one-off the user can't undo. */}
      <p className={css.settingsNote}>
        Change these any time in <Link to="/settings">Settings</Link>.
      </p>
    </div>
  );
};

export default WidenSearchStrip;
